"use client";

import { c as _c } from "react/compiler-runtime";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { IconBrainOutline18, IconCheckOutline18, IconCircleInfoOutline18, IconCodeBranchOutline18, IconDatabaseOutline18, IconMonitorOutline18, IconUsersOutline18 } from "nucleo-ui-outline-18";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { TierInfo } from "@/types/billing";
import type { PlanOption } from "@/types/planOptions";
import type { PricingTier } from "./pricing-data";
import { formatNumber, TIER_ICON_MAP } from "./pricing-data";
interface PricingCardProps {
  tier: PricingTier;
  backendTier?: TierInfo;
  isYearly: boolean;
  index: number;
  isCurrentTier?: boolean;
  currentTierId?: string;
  planOption?: PlanOption;
}
const CU_TOOLTIP = "Compute Units (CU) measure workflow execution usage. Simple actions like delays cost ~2 CU, while on-chain transactions can cost up to 500 CU.";
const TIER_ORDER = ["free", "starter", "pro", "biz", "enterprise"];
interface ResourceItem {
  icon: React.ComponentType<{
    className?: string;
  }>;
  value: string;
  label: string;
  hasCUTooltip?: boolean;
}
function buildResourceItems(backendTier?: TierInfo): ResourceItem[] {
  if (!backendTier) return [];
  return [{
    icon: IconCodeBranchOutline18,
    value: formatNumber(backendTier.maxActiveWorkflows),
    label: "active workflows"
  }, {
    icon: IconDatabaseOutline18,
    value: formatNumber(backendTier.maxDatabaseTables),
    label: "database tables"
  }, {
    icon: IconMonitorOutline18,
    value: formatNumber(backendTier.cuAmount),
    label: "CU/month",
    hasCUTooltip: true
  }, {
    icon: IconBrainOutline18,
    value: formatNumber(backendTier.aiCredits),
    label: "AI credits"
  }, {
    icon: IconUsersOutline18,
    value: backendTier.maxUsers === 0 ? "Unlimited" : String(backendTier.maxUsers),
    label: "users"
  }];
}
export function PricingCard(t0) {
  const $ = _c(85);
  const {
    tier,
    backendTier,
    isYearly,
    index,
    isCurrentTier,
    currentTierId,
    planOption
  } = t0;
  const currentTierIdx = currentTierId ? TIER_ORDER.indexOf(currentTierId) : -1;
  const thisTierIdx = TIER_ORDER.indexOf(tier.id);
  const isDowngrade = currentTierIdx > 0 && thisTierIdx < currentTierIdx;
  const ctaText = isDowngrade ? "Downgrade" : tier.ctaText;
  const isPopular = tier.popular;
  const rawMonthlyPrice = backendTier ? isYearly && backendTier.annualPriceUSDCents > 0 ? backendTier.annualPriceUSDCents / 100 / 12 : backendTier.priceUSDCents / 100 : null;
  const displayPrice = rawMonthlyPrice !== null ? `$${rawMonthlyPrice % 1 === 0 ? rawMonthlyPrice : rawMonthlyPrice.toFixed(2)}` : "\u2014";
  let T0;
  let isExternal;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== backendTier || $[1] !== displayPrice || $[2] !== index || $[3] !== isPopular || $[4] !== isYearly || $[5] !== planOption || $[6] !== tier.ctaHref || $[7] !== tier.description || $[8] !== tier.id || $[9] !== tier.name) {
    const resourceItems = buildResourceItems(backendTier);
    let t14;
    if ($[25] !== tier.ctaHref) {
      t14 = tier.ctaHref.startsWith("mailto:");
      $[25] = tier.ctaHref;
      $[26] = t14;
    } else {
      t14 = $[26];
    }
    isExternal = t14;
    T0 = motion.div;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        opacity: 0,
        y: 30
      };
      t9 = {
        opacity: 1,
        y: 0
      };
      $[27] = t8;
      $[28] = t9;
    } else {
      t8 = $[27];
      t9 = $[28];
    }
    const t15 = 0.3 + index * 0.1;
    if ($[29] !== t15) {
      t10 = {
        duration: 0.5,
        delay: t15
      };
      $[29] = t15;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    t11 = "border-ds-border-default bg-ds-neutral-100 relative flex flex-col rounded-[24px] border";
    if ($[31] !== isPopular) {
      t12 = isPopular && <img src="/popular-tag.png" alt="Popular" className="absolute right-[-23px] top-[-23px] z-10 w-[88px]" />;
      $[31] = isPopular;
      $[32] = t12;
    } else {
      t12 = $[32];
    }
    const t16 = TIER_ICON_MAP[tier.id] ?? TIER_ICON_MAP.free;
    const t17 = `${tier.name} plan icon`;
    let t18;
    if ($[33] !== t16 || $[34] !== t17) {
      t18 = <Image src={t16} alt={t17} width={16} height={16} className="size-4" />;
      $[33] = t16;
      $[34] = t17;
      $[35] = t18;
    } else {
      t18 = $[35];
    }
    let t19;
    if ($[36] !== tier.name) {
      t19 = <p className="text-ds-text-secondary font-mono text-base font-medium uppercase leading-[1.2] tracking-[-0.32px]">{tier.name}</p>;
      $[36] = tier.name;
      $[37] = t19;
    } else {
      t19 = $[37];
    }
    if ($[38] !== t18 || $[39] !== t19) {
      t13 = <div className="flex items-center gap-2 px-6 py-3">{t18}{t19}</div>;
      $[38] = t18;
      $[39] = t19;
      $[40] = t13;
    } else {
      t13 = $[40];
    }
    t6 = "border-ds-border-default flex flex-1 flex-col gap-3 rounded-[24px] border bg-white px-3 py-2.5";
    if ($[41] !== tier.description) {
      t7 = <div className="p-3"><p className="text-ds-text-primary h-[2em] text-sm leading-[1.2]">{tier.description}</p></div>;
      $[41] = tier.description;
      $[42] = t7;
    } else {
      t7 = $[42];
    }
    t3 = "bg-ds-surface-muted flex flex-col gap-2 overflow-hidden rounded-[20px] border border-black/10 p-3";
    let t20;
    if ($[43] !== backendTier || $[44] !== isYearly) {
      t20 = isYearly && backendTier && backendTier.priceUSDCents > 0 && <span className="text-ds-text-muted text-lg font-medium leading-[1.2] tracking-[-0.36px] line-through">${backendTier.priceUSDCents / 100}</span>;
      $[43] = backendTier;
      $[44] = isYearly;
      $[45] = t20;
    } else {
      t20 = $[45];
    }
    let t21;
    if ($[46] !== displayPrice) {
      t21 = <span className="text-ds-text-primary text-4xl font-semibold leading-[1.2] tracking-[-0.72px]">{displayPrice}</span>;
      $[46] = displayPrice;
      $[47] = t21;
    } else {
      t21 = $[47];
    }
    let t22;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <span className="text-ds-text-subtle text-[11px] leading-[1.2]">USD /<br />month</span>;
      $[48] = t22;
    } else {
      t22 = $[48];
    }
    if ($[49] !== t20 || $[50] !== t21) {
      t4 = <div className="flex items-center gap-2">{t20}{t21}{t22}</div>;
      $[49] = t20;
      $[50] = t21;
      $[51] = t4;
    } else {
      t4 = $[51];
    }
    if ($[52] !== planOption) {
      t5 = planOption && planOption.action === "downgrade" && planOption.effectiveAt && <p className="text-ds-text-muted text-2xs">Effective {new Date(planOption.effectiveAt).toLocaleDateString("en-US")}</p>;
      $[52] = planOption;
      $[53] = t5;
    } else {
      t5 = $[53];
    }
    t1 = "flex flex-col gap-1 py-2.5";
    t2 = resourceItems.map(_temp);
    $[0] = backendTier;
    $[1] = displayPrice;
    $[2] = index;
    $[3] = isPopular;
    $[4] = isYearly;
    $[5] = planOption;
    $[6] = tier.ctaHref;
    $[7] = tier.description;
    $[8] = tier.id;
    $[9] = tier.name;
    $[10] = T0;
    $[11] = isExternal;
    $[12] = t1;
    $[13] = t10;
    $[14] = t11;
    $[15] = t12;
    $[16] = t13;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    T0 = $[10];
    isExternal = $[11];
    t1 = $[12];
    t10 = $[13];
    t11 = $[14];
    t12 = $[15];
    t13 = $[16];
    t2 = $[17];
    t3 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
    t8 = $[23];
    t9 = $[24];
  }
  let t14;
  if ($[54] !== t1 || $[55] !== t2) {
    t14 = <div className={t1}>{t2}</div>;
    $[54] = t1;
    $[55] = t2;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  let t15;
  if ($[57] !== ctaText || $[58] !== isCurrentTier || $[59] !== isExternal || $[60] !== isPopular || $[61] !== tier.ctaHref) {
    t15 = <CTAButton isCurrentTier={isCurrentTier} isPopular={isPopular} isExternal={isExternal} ctaText={ctaText} ctaHref={tier.ctaHref} />;
    $[57] = ctaText;
    $[58] = isCurrentTier;
    $[59] = isExternal;
    $[60] = isPopular;
    $[61] = tier.ctaHref;
    $[62] = t15;
  } else {
    t15 = $[62];
  }
  let t16;
  if ($[63] !== t14 || $[64] !== t15 || $[65] !== t3 || $[66] !== t4 || $[67] !== t5) {
    t16 = <div className={t3}>{t4}{t5}{t14}{t15}</div>;
    $[63] = t14;
    $[64] = t15;
    $[65] = t3;
    $[66] = t4;
    $[67] = t5;
    $[68] = t16;
  } else {
    t16 = $[68];
  }
  let t17;
  if ($[69] !== tier.includedFeatures) {
    t17 = tier.includedFeatures.length > 0 && <div className="flex flex-col gap-2 px-3 pb-3 pt-2"><p className="text-ds-text-primary text-sm font-medium leading-[20px]">What's included:</p><div className="flex flex-col gap-1">{tier.includedFeatures.map(_temp2)}</div></div>;
    $[69] = tier.includedFeatures;
    $[70] = t17;
  } else {
    t17 = $[70];
  }
  let t18;
  if ($[71] !== t16 || $[72] !== t17 || $[73] !== t6 || $[74] !== t7) {
    t18 = <div className={t6}>{t7}{t16}{t17}</div>;
    $[71] = t16;
    $[72] = t17;
    $[73] = t6;
    $[74] = t7;
    $[75] = t18;
  } else {
    t18 = $[75];
  }
  let t19;
  if ($[76] !== T0 || $[77] !== t10 || $[78] !== t11 || $[79] !== t12 || $[80] !== t13 || $[81] !== t18 || $[82] !== t8 || $[83] !== t9) {
    t19 = <T0 initial={t8} animate={t9} transition={t10} className={t11}>{t12}{t13}{t18}</T0>;
    $[76] = T0;
    $[77] = t10;
    $[78] = t11;
    $[79] = t12;
    $[80] = t13;
    $[81] = t18;
    $[82] = t8;
    $[83] = t9;
    $[84] = t19;
  } else {
    t19 = $[84];
  }
  return t19;
}
function _temp2(feature) {
  return <div key={feature} className="flex items-center gap-2"><IconCheckOutline18 className="text-ds-text-subtle size-5 shrink-0" /><span className="text-ds-text-subtle text-sm leading-[1.2]">{feature}</span></div>;
}
function _temp(item) {
  return <div key={item.label} className="flex items-center gap-2"><item.icon className="text-ds-text-subtle size-5 shrink-0" /><span className="flex items-center gap-1 text-sm leading-[1.2]"><span className="text-ds-text-primary font-medium">{item.value}</span><span className="text-ds-text-subtle">{item.label}</span>{item.hasCUTooltip && <Tooltip><TooltipTrigger asChild={true}><button type="button" className="inline-flex align-middle"><IconCircleInfoOutline18 className="text-ds-text-muted hover:text-ds-text-tertiary size-3.5 transition-colors" /></button></TooltipTrigger><TooltipContent side="top" className="max-w-60">{CU_TOOLTIP}</TooltipContent></Tooltip>}</span></div>;
}
function CTAButton(t0) {
  const $ = _c(18);
  const {
    isCurrentTier,
    isPopular,
    isExternal,
    ctaText,
    ctaHref
  } = t0;
  if (isCurrentTier) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <button disabled={true} className="relative flex h-11 w-full items-center justify-center overflow-hidden rounded-xl opacity-60"><span aria-hidden="true" className="from-ds-neutral-150 to-ds-neutral-250 pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-b" /><span className="rounded-inherit pointer-events-none absolute inset-0 shadow-[inset_0px_1px_0px_0px_rgba(255,255,255,0.35)]" /><span className="text-ds-text-primary relative text-sm font-medium">Current Plan</span></button>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const buttonClass = isPopular ? "relative flex h-11 w-full items-center justify-center overflow-hidden rounded-xl shadow-[0px_0px_0px_1px_#1735b0,0px_3px_4px_-1px_rgba(53,130,255,0.95)]" : "relative flex h-11 w-full items-center justify-center overflow-hidden rounded-xl shadow-[0px_3px_4px_-1px_rgba(23,22,25,0.15),0px_0px_0px_1px_rgba(23,22,25,0.1)]";
  const bgClass = isPopular ? "from-ds-brand-500 to-ds-brand-600 pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-b" : "from-ds-neutral-150 to-ds-neutral-250 pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-b";
  const textClass = isPopular ? "relative text-sm font-medium text-white" : "text-ds-text-primary relative text-sm font-medium";
  let t1;
  if ($[1] !== bgClass) {
    t1 = <span aria-hidden="true" className={bgClass} />;
    $[1] = bgClass;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="rounded-inherit pointer-events-none absolute inset-0 shadow-[inset_0px_1px_0px_0px_rgba(255,255,255,0.35)]" />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== ctaText || $[5] !== textClass) {
    t3 = <span className={textClass}>{ctaText}</span>;
    $[4] = ctaText;
    $[5] = textClass;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <>{t1}{t2}{t3}</>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const inner = t4;
  if (isExternal) {
    let t5;
    if ($[10] !== buttonClass || $[11] !== ctaHref || $[12] !== inner) {
      t5 = <a href={ctaHref} className={buttonClass}>{inner}</a>;
      $[10] = buttonClass;
      $[11] = ctaHref;
      $[12] = inner;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  let t5;
  if ($[14] !== buttonClass || $[15] !== ctaHref || $[16] !== inner) {
    t5 = <Link href={ctaHref} className={buttonClass}>{inner}</Link>;
    $[14] = buttonClass;
    $[15] = ctaHref;
    $[16] = inner;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}